import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, User, Phone, Mail, Calendar, Globe, MapPin, Briefcase, Wrench, PencilLine, Upload, AlertCircle, ArrowRight } from "lucide-react";
import MmpLayout from "./MmpLayout";
import { COUNTRIES, STATES, PROFESSIONS } from "@/data/mmp";
import api from "@/lib/api";

const REQUIRED_TEXT = [
  ["full_name", "Full Name"],
  ["phone", "Phone Number"],
  ["email", "Email Address"],
  ["gender", "Gender"],
  ["date_of_birth", "Date of Birth"],
  ["age", "Age"],
  ["country", "Country"],
  ["state", "State/Region"],
  ["address", "Home Address"],
  ["profession", "Profession"],
  ["experience", "Years of Experience"],
];

export default function MmpSubmit() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", gender: "", date_of_birth: "", age: "",
    country: "", state: "", city: "", address: "",
    profession: "", other_profession: "", experience: "", skills: "", summary: "",
  });
  const [files, setFiles] = useState({ cv: null, photo: null, portfolio: null });
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const on = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v, ...(k === "country" ? { state: "" } : {}) }));
  };
  const onFile = (k) => (e) => setFiles((f) => ({ ...f, [k]: e.target.files?.[0] || null }));

  const stateList = useMemo(() => STATES[form.country] || [], [form.country]);

  const validate = () => {
    const errs = [];
    for (const [k, label] of REQUIRED_TEXT) {
      if (!form[k]) errs.push(`${label} is required`);
    }
    if (form.profession === "Other" && !form.other_profession.trim()) errs.push("Please specify your profession");
    const age = Number(form.age);
    if (form.age && (age < 16 || age > 80)) errs.push("Age must be between 16 and 80");
    if (!files.cv) errs.push("CV (PDF) is required");
    else if (!files.cv.name.toLowerCase().endsWith(".pdf")) errs.push("CV must be a PDF file");
    else if (files.cv.size > 10 * 1024 * 1024) errs.push("CV must be under 10 MB");
    if (!files.photo) errs.push("Passport Photograph is required");
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (errs.length) { setErrors(errs); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setErrors([]);
    setSubmitting(true);
    try {
      const payload = { ...form, age: Number(form.age) };
      // Two-step: create record first
      let uniqueId;
      try {
        const { data } = await api.post("/profiles/submit-info", payload, { timeout: 120000 });
        uniqueId = data.unique_id;
        const uploadCfg = { timeout: 120000, headers: { "Content-Type": "multipart/form-data" } };
        const cvFd = new FormData(); cvFd.append("file", files.cv);
        await api.post(`/profiles/upload-cv/${uniqueId}`, cvFd, uploadCfg);
        const phFd = new FormData(); phFd.append("file", files.photo);
        await api.post(`/profiles/upload-photo/${uniqueId}`, phFd, uploadCfg);
        if (files.portfolio) {
          const poFd = new FormData(); poFd.append("file", files.portfolio);
          await api.post(`/profiles/upload-portfolio/${uniqueId}`, poFd, uploadCfg);
        }
      } catch (twoStepErr) {
        // Fallback: single-step combined
        const fd = new FormData();
        fd.append("payload", JSON.stringify(payload));
        fd.append("cv", files.cv);
        fd.append("photo", files.photo);
        if (files.portfolio) fd.append("portfolio", files.portfolio);
        const { data } = await api.post("/profiles/submit", fd, { timeout: 120000, headers: { "Content-Type": "multipart/form-data" } });
        uniqueId = data.unique_id;
      }
      toast.success("Submission received — check your Unique ID.");
      navigate(`/careers/success/${uniqueId}`, { state: { email: form.email } });
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Submission failed";
      setErrors([detail]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MmpLayout>
      <div className="mia-container py-10 lg:py-14 max-w-3xl">
        <div className="text-[0.62rem] font-bold tracking-[0.24em] uppercase text-sky-400">Step 1 of 1</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl mt-3 tracking-tight">Submit Your Profile</h1>
        <p className="text-slate-400 mt-2">Complete the form below. It takes less than 2 minutes.</p>

        {errors.length > 0 && (
          <div className="mt-6 rounded-lg border border-red-500/40 bg-red-500/10 text-red-200 p-4 space-y-1" data-testid="mmp-errors">
            <div className="flex items-center gap-2 font-semibold"><AlertCircle className="w-4 h-4" /> Please fix the following:</div>
            <ul className="text-sm ml-6 list-disc">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-8" data-testid="mmp-submit-form">
          <Section title="1 · Personal Information">
            <Field icon={User} label="Full Name" required value={form.full_name} onChange={on("full_name")} testid="mmp-full-name" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field icon={Phone} label="Phone Number" required value={form.phone} onChange={on("phone")} testid="mmp-phone" />
              <Field icon={Mail} label="Email Address" required type="email" value={form.email} onChange={on("email")} testid="mmp-email" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <SelectField icon={User} label="Gender" required value={form.gender} onChange={on("gender")} testid="mmp-gender">
                <option value="">Select…</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </SelectField>
              <Field icon={Calendar} label="Date of Birth" required type="date" value={form.date_of_birth} onChange={on("date_of_birth")} testid="mmp-dob" />
              <Field icon={User} label="Age" required type="number" min={16} max={80} value={form.age} onChange={on("age")} testid="mmp-age" />
            </div>
          </Section>

          <Section title="2 · Location">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectField icon={Globe} label="Country" required value={form.country} onChange={on("country")} testid="mmp-country">
                <option value="">Select country…</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </SelectField>
              {stateList.length > 0 ? (
                <SelectField icon={MapPin} label="State / Region" required value={form.state} onChange={on("state")} disabled={!form.country} testid="mmp-state">
                  <option value="">{form.country ? "Select state…" : "Select country first"}</option>
                  {stateList.map((s) => <option key={s} value={s}>{s}</option>)}
                </SelectField>
              ) : (
                <Field icon={MapPin} label="State / Region" required value={form.state} onChange={on("state")} disabled={!form.country} placeholder={form.country ? "Enter state / region" : "Select country first"} testid="mmp-state-text" />
              )}
            </div>
            <Field icon={MapPin} label="Home Address" required value={form.address} onChange={on("address")} testid="mmp-address" />
          </Section>

          <Section title="3 · Professional Details">
            <SelectField icon={Briefcase} label="Profession" required value={form.profession} onChange={on("profession")} testid="mmp-profession">
              <option value="">Select profession…</option>
              {PROFESSIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </SelectField>
            {form.profession === "Other" && (
              <Field icon={Briefcase} label="Specify Profession" required value={form.other_profession} onChange={on("other_profession")} testid="mmp-other-profession" />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field icon={Wrench} label="Years of Experience" required placeholder="e.g. 8 years" value={form.experience} onChange={on("experience")} testid="mmp-experience" />
              <Field icon={Wrench} label="Skills (comma-separated)" value={form.skills} onChange={on("skills")} testid="mmp-skills" />
            </div>
            <div>
              <Label>Short Summary</Label>
              <div className="mt-2 rounded-lg border border-white/10 bg-[#0B0F1A] px-4 py-3 focus-within:border-sky-500 flex items-start gap-3">
                <PencilLine className="w-4 h-4 text-slate-500 mt-1 shrink-0" />
                <textarea rows={3} value={form.summary} onChange={on("summary")} className="w-full bg-transparent focus:outline-none text-sm text-slate-100 resize-none" data-testid="mmp-summary" placeholder="2-3 lines about your marine/maritime expertise…" />
              </div>
            </div>
          </Section>

          <Section title="4 · Upload Documents">
            <UploadField label="Passport Photograph" accept="image/*" required onChange={onFile("photo")} file={files.photo} testid="mmp-file-photo" />
            <UploadField label="CV (PDF, max 10 MB)" accept="application/pdf" required onChange={onFile("cv")} file={files.cv} testid="mmp-file-cv" />
            <UploadField label="Portfolio (PDF or image, optional)" accept="application/pdf,image/*" onChange={onFile("portfolio")} file={files.portfolio} testid="mmp-file-portfolio" />
          </Section>

          <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-[#0B0F1A] font-bold text-sm uppercase tracking-[0.14em] px-6 py-4 rounded-lg transition-colors" data-testid="mmp-form-submit">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit Profile <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </MmpLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0E1525] p-6 sm:p-7 space-y-4">
      <div className="text-[0.62rem] font-bold tracking-[0.24em] uppercase text-sky-400">{title}</div>
      {children}
    </div>
  );
}

function Label({ children }) {
  return <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">{children}</label>;
}

function Field({ icon: Icon, label, required, type = "text", value, onChange, disabled, placeholder, testid, min, max }) {
  return (
    <div>
      <Label>{label}{required && " *"}</Label>
      <div className={`mt-2 rounded-lg border border-white/10 bg-[#0B0F1A] px-4 py-3 focus-within:border-sky-500 flex items-center gap-3 ${disabled ? "opacity-60" : ""}`}>
        <Icon className="w-4 h-4 text-slate-500 shrink-0" />
        <input type={type} required={required} value={value} onChange={onChange} disabled={disabled} placeholder={placeholder} min={min} max={max} className="w-full bg-transparent focus:outline-none text-sm text-slate-100" data-testid={testid} />
      </div>
    </div>
  );
}

function SelectField({ icon: Icon, label, required, value, onChange, disabled, children, testid }) {
  return (
    <div>
      <Label>{label}{required && " *"}</Label>
      <div className={`mt-2 rounded-lg border border-white/10 bg-[#0B0F1A] px-4 py-3 focus-within:border-sky-500 flex items-center gap-3 ${disabled ? "opacity-60" : ""}`}>
        <Icon className="w-4 h-4 text-slate-500 shrink-0" />
        <select required={required} value={value} onChange={onChange} disabled={disabled} className="w-full bg-transparent focus:outline-none text-sm text-slate-100" data-testid={testid}>
          {children}
        </select>
      </div>
    </div>
  );
}

function UploadField({ label, accept, required, onChange, file, testid }) {
  return (
    <div>
      <Label>{label}{required && " *"}</Label>
      <label className="mt-2 flex items-center gap-3 rounded-lg border border-dashed border-white/15 bg-[#0B0F1A] px-4 py-4 cursor-pointer hover:border-sky-500 transition-colors">
        <Upload className="w-4 h-4 text-sky-400 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm text-slate-200 truncate">{file ? file.name : "Click to select a file"}</div>
          {file && <div className="text-[0.65rem] text-slate-500 mt-0.5">{(file.size / 1024).toFixed(0)} KB</div>}
        </div>
        <input type="file" accept={accept} required={required && !file} onChange={onChange} className="hidden" data-testid={testid} />
      </label>
    </div>
  );
}
